import type { Path } from '@cobliteam/api-lib/src/__generated__/graphql';
import useVehiclePastAssociations from '@cobliteam/hooks/src/useEntity/useVehiclePastAssociations';
import { useTranslation } from '@cobliteam/i18n';
import Divider from '@cobliteam/ui/components/Divider';
import Drawer, { DrawerBody, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle } from '@cobliteam/ui/components/Drawer';
import Typography from '@cobliteam/ui/components/Typography';
import { formatISO } from '@cobliteam/utils/date';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo } from 'react';
import AssociationHistoryItem from './components/AssociationHistoryItem';
import DrawerLoadingSkeleton from './components/DrawerLoadingSkeleton';

interface AssociationHistoryDrawerProps {
  path: Path;
  open: boolean;
  onClose: () => void;
}
const AssociationHistoryDrawer = ({ path, open, onClose }: AssociationHistoryDrawerProps) => {
  const { t } = useTranslation('path-association');
  const vehicleId = path.vehicle?.id;

  const { data, isFetching } = useVehiclePastAssociations(
    {
      vehicleId: vehicleId!,
      referenceTimestamp: formatISO(path.startTime + 1000),
    },
    { enabled: open && !!vehicleId }
  );

  const associationsToDisplay = useMemo(() => {
    if (!data) {
      return [];
    }

    return data
      .map((association, idx) => {
        const nextAssociation = idx < data.length - 1 ? data[idx + 1] : undefined;

        return {
          ...association,
          previousDriver: nextAssociation?.driver,
        };
      })
      .filter((association) => association.previousDriver || association.driver);
  }, [data]);

  const hasAssociations = associationsToDisplay.length > 0;
  const isLoading = isFetching && !data;
  const showEmptyState = !(isLoading || hasAssociations);

  return (
    <Drawer open={open} onClose={onClose}>
      <DrawerContent size='sm'>
        <DrawerHeader>
          <DrawerTitle>{t('association_history.title')}</DrawerTitle>
          <DrawerClose />
        </DrawerHeader>
        <DrawerBody className='flex-col gap-4 overflow-y-auto'>
          <AnimatePresence mode='wait'>
            {isLoading && (
              <motion.div key='loading' initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <DrawerLoadingSkeleton />
              </motion.div>
            )}
            {showEmptyState && (
              <motion.div
                key='empty'
                className='flex items-center justify-center py-8'
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <Typography color='secondary'>{t('association_history.empty_state')}</Typography>
              </motion.div>
            )}
            {!isLoading && hasAssociations && (
              <motion.div key='list' initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                <motion.div
                  initial='hidden'
                  animate='visible'
                  variants={{
                    visible: {
                      transition: {
                        staggerChildren: 0.1,
                      },
                    },
                  }}
                  className='flex flex-col gap-4'
                >
                  {associationsToDisplay.map((association, idx) => (
                    <motion.div
                      key={`${association.associatedAt}-${idx}`}
                      className='flex flex-col gap-4'
                      variants={{
                        hidden: { opacity: 0, y: 20 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.3,
                            ease: 'easeOut',
                          },
                        },
                      }}
                    >
                      <AssociationHistoryItem association={association} />
                      {idx < associationsToDisplay.length - 1 && <Divider />}
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default AssociationHistoryDrawer;
